/**
 * batch-58 klant-portaal-beheer — gedeelde types, defaults en Zod-schemas voor:
 *
 *   FO-428  overeenkomsten-/documenten-vrijgave + de gebundelde portaal-sectie
 *   FO-427  uitrol-zichtbaarheid (status-overzicht + activatie-checklist)
 *   FO-426 P6  per-pagina-rechten op een `ClientPortalAccount`
 *   FO-426 P8  de klant-pagina "Afspraken" + de deel-vlaggen erachter
 *
 * Eén bron voor API, backoffice en klant-app: de API valideert met exact de
 * schemas die de formulieren client-side gebruiken (CLAUDE.md "Validatie &
 * foutmeldingen"). De NL-labels horen bij de BACKOFFICE; de klant-app is
 * tweetalig en haalt zijn teksten uit next-intl.
 */
import { z } from 'zod'
import { queryBoolean } from './zod-query'

// ─────────────────────────────────────────────────────────────────────────────
// FO-426 P6 — per-pagina-rechten op het klant-account
// ─────────────────────────────────────────────────────────────────────────────

/** Rollen van een `ClientPortalAccount` (spiegelt de Prisma-enum). */
export const KLANT_PORTAAL_ROLLEN = ['OWNER', 'ADMIN', 'FINANCE', 'READONLY'] as const
export type KlantPortaalRol = (typeof KLANT_PORTAAL_ROLLEN)[number]

/**
 * Toegangsniveau per pagina. Drie standen in plaats van een boolean, omdat de
 * klant-app twee soorten toegang kent die de PO uit elkaar moet kunnen zetten:
 * meekijken (FINANCE/READONLY) en zelf invullen (OWNER/ADMIN).
 */
export const KLANT_PAGINA_NIVEAUS = ['GEEN', 'LEZEN', 'BEWERKEN'] as const
export type KlantPaginaNiveau = (typeof KLANT_PAGINA_NIVEAUS)[number]

export const KLANT_PAGINA_NIVEAU_LABELS: Record<KlantPaginaNiveau, string> = {
  GEEN: 'Geen toegang',
  LEZEN: 'Alleen lezen',
  BEWERKEN: 'Lezen en invullen',
}

/**
 * De pagina's waarvoor rechten instelbaar zijn.
 *
 * **Deze lijst bevat UITSLUITEND pagina's waarvan de API het recht ook echt
 * afdwingt.** Een sleutel toevoegen zonder `requireKlantPagina(<sleutel>, ...)`
 * op de bijbehorende route levert een half slot op: het beheerscherm belooft
 * dan iets dat een klant met een directe URL omzeilt. De drift-guard
 * `apps/api/src/lib/__tests__/klant-pagina-rechten-drift.test.ts` faalt op zo'n
 * sleutel, zodat de belofte en de handhaving niet uit elkaar kunnen lopen.
 *
 * `facturen`, `werkbonnen` en `planning` zijn in de vervolg-FO toegevoegd, mét
 * hun gates: de belofte in het beheerscherm ("een account de facturen afnemen")
 * stond er wel, maar de sleutel niet - een beheerder kon het dus niet.
 *
 * Nog steeds NIET instelbaar (bewust): factuurverzoeken (facturatie-overzichten
 * accorderen is de kern-taak van een FINANCE-account), vervanging en aanvragen.
 * Dashboard, account en notificaties horen bij elk account en staan er daarom
 * principieel niet bij.
 */
export const KLANT_PORTAAL_PAGINAS = [
  'werkbonnen',
  'planning',
  'facturen',
  'offertes',
  'monteurkeuze',
  'monteursprofielen',
  'beoordelingen',
  'documenten',
  'afspraken',
  'veiligheid',
  'installaties',
  'gereedschap',
] as const
export type KlantPortaalPagina = (typeof KLANT_PORTAAL_PAGINAS)[number]

/** Backoffice-labels van de pagina's (de klant-app gebruikt next-intl). */
export const KLANT_PORTAAL_PAGINA_LABELS: Record<KlantPortaalPagina, string> = {
  werkbonnen: 'Werkbonnen',
  planning: 'Planning (week, jaar, afwezigheid, inzet, sluitingsdagen)',
  facturen: 'Facturen',
  offertes: 'Offertes',
  monteurkeuze: 'Beschikbare monteurs',
  monteursprofielen: 'Monteursprofielen',
  beoordelingen: 'Beoordelingen',
  documenten: 'Documenten en overeenkomsten',
  afspraken: 'Afspraken',
  veiligheid: 'Veiligheid',
  installaties: 'Installaties',
  gereedschap: 'Gereedschap',
}

function alleNiveaus(niveau: KlantPaginaNiveau): Record<KlantPortaalPagina, KlantPaginaNiveau> {
  return Object.fromEntries(KLANT_PORTAAL_PAGINAS.map((p) => [p, niveau])) as Record<
    KlantPortaalPagina,
    KlantPaginaNiveau
  >
}

/**
 * DE GEDOCUMENTEERDE DEFAULTS per rol (spec §3.4: "met gedocumenteerde
 * defaults"). Ze reproduceren EXACT het gedrag van vóór batch-58:
 *
 *   OWNER/ADMIN      mochten overal kijken én invullen  -> BEWERKEN
 *   FINANCE/READONLY mochten overal kijken, niet invullen -> LEZEN
 *
 * Daarom is er geen backfill nodig: een account zonder afwijkingen
 * (`paginaRechten = null`) houdt precies de toegang die het had. De rechtenlaag
 * is een AANVULLING waarmee een beheerder per account kan afwijken - bv. een
 * READONLY-inkoper tóch beoordelingen laten invullen, of een ADMIN de facturen
 * afnemen.
 */
export const KLANT_PAGINA_ROL_DEFAULTS: Record<
  KlantPortaalRol,
  Record<KlantPortaalPagina, KlantPaginaNiveau>
> = {
  OWNER: alleNiveaus('BEWERKEN'),
  ADMIN: alleNiveaus('BEWERKEN'),
  FINANCE: alleNiveaus('LEZEN'),
  READONLY: alleNiveaus('LEZEN'),
}

/**
 * Alleen de AFWIJKINGEN worden opgeslagen; ontbrekende sleutels = roldefault.
 * `z.record` met een enum-sleutel infereert een VOLLEDIG `Record`, terwijl zowel
 * de runtime als elke aanroeper een partial verwacht - vandaar de expliciete
 * `.partial()`-typering, zodat het type de werkelijkheid volgt.
 */
export const klantPaginaRechtenSchema: z.ZodType<KlantPaginaRechten> = z.record(
  z.enum(KLANT_PORTAAL_PAGINAS),
  z.enum(KLANT_PAGINA_NIVEAUS),
)
export type KlantPaginaRechten = Partial<Record<KlantPortaalPagina, KlantPaginaNiveau>>
export type KlantPaginaRechtenInput = KlantPaginaRechten

/** Payload van het rechtenblok in het backoffice-gebruikersbeheer. */
export const klantPaginaRechtenUpdateSchema = z.object({
  /** `null` = terug naar het roldefault (alle afwijkingen wissen). */
  paginaRechten: klantPaginaRechtenSchema.nullable(),
})
export type KlantPaginaRechtenUpdateInput = z.infer<typeof klantPaginaRechtenUpdateSchema>

/**
 * De opgeslagen JSON is `unknown` tot hij gevalideerd is - nooit blind casten
 * (CLAUDE.md: geen `any`). Onbekende of vervallen sleutels worden genegeerd,
 * zodat een verwijderde pagina geen kapotte rechten achterlaat.
 */
export function parseKlantPaginaRechten(raw: unknown): KlantPaginaRechten {
  if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const uit: KlantPaginaRechten = {}
  for (const [key, waarde] of Object.entries(raw as Record<string, unknown>)) {
    if (!(KLANT_PORTAAL_PAGINAS as readonly string[]).includes(key)) continue
    if (typeof waarde !== 'string') continue
    if (!(KLANT_PAGINA_NIVEAUS as readonly string[]).includes(waarde)) continue
    uit[key as KlantPortaalPagina] = waarde as KlantPaginaNiveau
  }
  return uit
}

/** Roldefault + opgeslagen afwijkingen -> het effectieve rechtenbeeld. */
export function resolveKlantPaginaRechten(
  rol: KlantPortaalRol,
  opgeslagen: unknown,
): Record<KlantPortaalPagina, KlantPaginaNiveau> {
  return { ...KLANT_PAGINA_ROL_DEFAULTS[rol], ...parseKlantPaginaRechten(opgeslagen) }
}

const NIVEAU_RANG: Record<KlantPaginaNiveau, number> = { GEEN: 0, LEZEN: 1, BEWERKEN: 2 }

/** Haalt dit account het gevraagde niveau op deze pagina? */
export function heeftKlantPaginaNiveau(
  rechten: Record<KlantPortaalPagina, KlantPaginaNiveau>,
  pagina: KlantPortaalPagina,
  vereist: Exclude<KlantPaginaNiveau, 'GEEN'>,
): boolean {
  return NIVEAU_RANG[rechten[pagina]] >= NIVEAU_RANG[vereist]
}

// ─────────────────────────────────────────────────────────────────────────────
// FO-428 — gebundelde sectie "Documenten & overeenkomsten"
// ─────────────────────────────────────────────────────────────────────────────

/** De drie tabbladen van de gebundelde sectie (spec §3.2). */
export const KLANT_STUK_TABS = ['OVEREENKOMSTEN', 'OPDRACHTBEVESTIGINGEN', 'OVERIG'] as const
export type KlantStukTab = (typeof KLANT_STUK_TABS)[number]

/** Uit welk systeem een stuk komt (bron-badge in de beheer-lijst). */
export const KLANT_STUK_BRONNEN = ['OVEREENKOMST', 'DOCUMENT'] as const
export type KlantStukBron = (typeof KLANT_STUK_BRONNEN)[number]

export const KLANT_STUK_BRON_LABELS: Record<KlantStukBron, string> = {
  OVEREENKOMST: 'Overeenkomst',
  DOCUMENT: 'Document',
}

/**
 * De klant-status van een stuk. Bewust een EIGEN, kleine set: de interne
 * `ClientContractStatus` (INTERN_AKKOORD, OPGEZEGD, ...) is interne semantiek
 * en hoort niet in een klant-payload.
 */
export const KLANT_STUK_STATUSSEN = [
  'GEDEELD',
  'WACHT_OP_ONDERTEKENING',
  'GETEKEND',
  'VERLOPEN',
] as const
export type KlantStukStatus = (typeof KLANT_STUK_STATUSSEN)[number]

/**
 * Eén stuk in de gebundelde klant-sectie. Veld-allowlist: geen contractbedrag,
 * indexeringspercentage, interne notitie, verantwoordelijke of andere interne
 * velden (spec §6.3 payload-scan).
 */
export interface KlantStukDTO {
  /** `<bron>:<id>` - stabiel over beide bronnen heen. */
  sleutel: string
  bron: KlantStukBron
  id: string
  titel: string
  /** Vertaalsleutel-achtige code (contracttype of documentcategorie). */
  soort: string
  status: KlantStukStatus
  /** ISO-datum: ingangsdatum (overeenkomst) of uploaddatum (document). */
  datum: string
  /** ISO-datum einddatum van een overeenkomst, anders null. */
  einddatum: string | null
  /** Loopt binnen 60 dagen af (alleen overeenkomsten). */
  afloopBinnenkort: boolean
  /** Kan de klant het stuk openen/downloaden? */
  heeftDocument: boolean
  /**
   * Same-origin pad in de klant-app (nooit het API-origin: een browser-
   * navigatie draagt geen Bearer). `null` als er niets te openen valt.
   */
  downloadPad: string | null
}

export interface KlantStukkenMeta {
  tab: KlantStukTab
  page: number
  pageSize: number
  /** Server-side totaal over de VOLLEDIGE gefilterde set. */
  total: number
  /** Aantallen per tab, voor de tab-badges. */
  aantallen: Record<KlantStukTab, number>
}

// ── Beheer-kant: één gecombineerde lijst met effectieve zichtbaarheid ────────

/** Eén regel in de beheer-lijst op de relatie-tab Klantenportaal. */
export interface BeheerStukDTO {
  sleutel: string
  bron: KlantStukBron
  id: string
  titel: string
  soort: string
  soortLabel: string
  /** Interne status (beheerder mag die wél zien). */
  statusLabel: string
  datum: string
  zichtbaar: boolean
  /** Waarom (`FLOW`, `OVERRIDE_VRIJGEGEVEN`, `CATEGORIE`, ...). */
  bronVanZichtbaarheid: string
  bronVanZichtbaarheidLabel: string
  /** Drie standen van de override (`null` = volgt flow/categorie). */
  override: boolean | null
  /** Verbergen kan niet tijdens een lopend tekenverzoek. */
  magVerbergen: boolean
  verbergenGeblokkeerdReden: string | null
  /** Een concept kan nooit vrijgegeven worden. */
  magVrijgeven: boolean
  /**
   * batch-60 (FO-439b K1) — de reden waarom het stuk verborgen is; alleen
   * gevuld bij een verberg-override. Het scherm toont "Verborgen (reden)".
   */
  verbergReden: string | null
}

export const VRIJGAVE_BRON_LABELS: Record<string, string> = {
  INTERN: 'Interne fase',
  FLOW: 'Via de flow gedeeld',
  // batch-60 (FO-439b K1) — de nieuwe default: niet-concept = zichtbaar.
  STANDAARD: 'Standaard zichtbaar (niet-concept)',
  OVERRIDE_VRIJGEGEVEN: 'Handmatig vrijgegeven',
  OVERRIDE_VERBORGEN: 'Verborgen voor klant',
  CATEGORIE: 'Via categorie-instelling',
  NIET_GEDEELD: 'Niet gedeeld (buiten categorie-instelling)',
}

/** Vrijgeven/verbergen van één stuk (overeenkomst of document). */
// batch-60 (FO-439b K1) — verbergen is een bewuste, geauditeerde uitzondering:
// zonder reden (min. 3 tekens) weigert de API én het formulier. Vrijgeven en
// terugzetten naar de standaard blijven zonder reden mogelijk.
export const KLANT_STUK_VERBERG_REDEN_MIN = 3
export const klantStukVrijgaveSchema = z
  .object({
    /** `true` = vrijgeven, `false` = verbergen, `null` = terug naar de standaard/categorie. */
    vrijgeven: z.boolean().nullable(),
    reden: z.string().trim().max(1000).optional().nullable(),
  })
  .refine(
    (v) => v.vrijgeven !== false || (v.reden ?? '').trim().length >= KLANT_STUK_VERBERG_REDEN_MIN,
    { message: 'Geef een reden om dit stuk voor de klant te verbergen', path: ['reden'] },
  )
export type KlantStukVrijgaveInput = z.infer<typeof klantStukVrijgaveSchema>

// ─────────────────────────────────────────────────────────────────────────────
// FO-427 — activatie-checklist per klant
// ─────────────────────────────────────────────────────────────────────────────

/**
 * De uitrolstappen uit het runbook (spec §A3), als afvinkbaar lijstje per klant.
 * De volgorde hier is de volgorde in het scherm. Een nieuwe stap toevoegen is
 * puur een code-wijziging: onbekende sleutels in de opgeslagen JSON worden
 * genegeerd en een nieuwe sleutel verschijnt als "nog niet gedaan".
 */
export const KLANT_ACTIVATIE_STAPPEN = [
  {
    key: 'instellingen',
    label: 'Portaal-instellingen doorlopen (facturen, documenten, drempels)',
  },
  { key: 'documentcategorieen', label: 'Documentcategorieën gekozen of stukken vrijgegeven' },
  { key: 'gereedschapZichtbaar', label: 'Gereedschapspagina aangezet en lijst gevuld' },
  { key: 'monteurkeuzeZichtbaar', label: 'Monteurkeuze aangezet en monteurs vrijgegeven' },
  { key: 'afspraken', label: 'Afspraken-pagina ingericht (deel-vlaggen gezet)' },
  { key: 'accounts', label: 'Portaal-accounts aangemaakt en rechten gezet' },
  { key: 'tour', label: 'Rondleiding en gegevenscontrole met een testaccount doorlopen' },
  { key: 'klantGeinformeerd', label: 'Klant geïnformeerd dat het portaal live staat' },
] as const
export type KlantActivatieStapKey = (typeof KLANT_ACTIVATIE_STAPPEN)[number]['key']

export interface KlantActivatieStapStand {
  gedaan: boolean
  /** ISO-timestamp van het afvinken, of null. */
  op: string | null
  doorUserId: string | null
  notitie: string | null
}

const activatieStapSchema = z.object({
  gedaan: z.boolean(),
  op: z.string().nullable().optional(),
  doorUserId: z.string().nullable().optional(),
  notitie: z.string().trim().max(1000).nullable().optional(),
})

/** Het opgeslagen JSON-veld `Client.portaalActivatieChecklist`. */
export const klantActivatieChecklistSchema = z.record(z.string(), activatieStapSchema)

/** Wat het scherm stuurt: één stap aan- of afvinken. */
export const klantActivatieStapSchema = z.object({
  stap: z.enum(
    KLANT_ACTIVATIE_STAPPEN.map((s) => s.key) as unknown as [
      KlantActivatieStapKey,
      ...KlantActivatieStapKey[],
    ],
  ),
  gedaan: z.boolean(),
  notitie: z.string().trim().max(1000).nullable().optional(),
})
export type KlantActivatieStapInput = z.infer<typeof klantActivatieStapSchema>

/** Opgeslagen JSON -> een volledige, veilige stand per bekende stap. */
export function parseKlantActivatieChecklist(
  raw: unknown,
): Record<KlantActivatieStapKey, KlantActivatieStapStand> {
  const leeg: KlantActivatieStapStand = { gedaan: false, op: null, doorUserId: null, notitie: null }
  const uit = Object.fromEntries(
    KLANT_ACTIVATIE_STAPPEN.map((s) => [s.key, { ...leeg }]),
  ) as Record<KlantActivatieStapKey, KlantActivatieStapStand>
  const parsed = klantActivatieChecklistSchema.safeParse(raw)
  if (!parsed.success) return uit
  for (const [key, waarde] of Object.entries(parsed.data)) {
    if (!(key in uit)) continue
    uit[key as KlantActivatieStapKey] = {
      gedaan: waarde.gedaan,
      op: waarde.op ?? null,
      doorUserId: waarde.doorUserId ?? null,
      notitie: waarde.notitie ?? null,
    }
  }
  return uit
}

// ─────────────────────────────────────────────────────────────────────────────
// FO-427 §B4 — status-overzicht "Klantenportaal-status"
// ─────────────────────────────────────────────────────────────────────────────

/** Eén klant-regel in het read-only uitrol-overzicht. */
export interface KlantPortaalStatusRow {
  clientId: string
  clientNumber: string | null
  naam: string
  facturenZichtbaar: boolean
  facturenModus: string
  documentCategorieenAuto: number
  gereedschapZichtbaar: boolean
  monteurkeuzeZichtbaar: boolean
  afsprakenZichtbaar: boolean
  /** Aantal actieve portaal-accounts van deze klant. */
  accounts: number
  /** Accounts die de rondleiding hebben afgerond of overgeslagen. */
  tourAfgerond: number
  /** Heeft de klant de gegevens-controlestap (D2) afgerond? */
  controleAfgerond: boolean
  vrijgegevenMonteurs: number
  vrijgegevenDocumenten: number
  zichtbareOvereenkomsten: number
  /** ISO-timestamp van de laatste D5-cacheberekening voor deze klant, of null. */
  laatsteCacheBerekening: string | null
  /** Hoeveel van de activatie-checklist is afgevinkt. */
  checklistGedaan: number
  checklistTotaal: number
}

export const klantPortaalStatusQuerySchema = z.object({
  zoek: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  /** Alleen klanten met minstens één portaal-account. */
  alleenMetAccounts: queryBoolean().optional(),
})
export type KlantPortaalStatusQuery = z.infer<typeof klantPortaalStatusQuerySchema>

// ─────────────────────────────────────────────────────────────────────────────
// FO-426 P8 — afspraken (deel-vlaggen + klant-DTO)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * De deel-vlaggen op de relatie. KOSTPRIJS EN MARGE STAAN HIER BEWUST NIET EN
 * KRIJGEN NOOIT EEN VLAG (spec §4: "kostprijs/marge nooit deelbaar - harde
 * uitsluiting in de DTO").
 */
export const klantAfsprakenDelenSchema = z.object({
  portaalAfsprakenZichtbaar: z.boolean(),
  portaalDeelTarieven: z.boolean(),
  portaalDeelBetalingstermijn: z.boolean(),
  portaalDeelFacturatieroute: z.boolean(),
  portaalDeelWerktijden: z.boolean(),
  portaalAfsprakenToelichting: z.string().trim().max(5000).nullable().optional(),
})
export type KlantAfsprakenDelenInput = z.infer<typeof klantAfsprakenDelenSchema>

export const KLANT_AFSPRAKEN_DEEL_LABELS: Record<
  Exclude<
    keyof KlantAfsprakenDelenInput,
    'portaalAfsprakenZichtbaar' | 'portaalAfsprakenToelichting'
  >,
  string
> = {
  portaalDeelTarieven: 'Tarieflijst-namen en geldigheid (nooit bedragen van kostprijs of marge)',
  portaalDeelBetalingstermijn: 'Betalingstermijn',
  portaalDeelFacturatieroute: 'Facturatieroute (hoe de factuur bij de klant komt)',
  portaalDeelWerktijden: 'Werktijden en pauzes uit het werkschema',
}

/** Eén gedeelde tarieflijst-regel in de klant-DTO (LABELS, geen bedragen). */
export interface KlantAfspraakTariefDTO {
  id: string
  naam: string
  /** Werkbontype waarop de lijst van toepassing is, of null. */
  werkbonType: string | null
  /** Ploegsoort/dienstsoort waarop de lijst van toepassing is, of null. */
  shiftType: string | null
  isStandaard: boolean
  /** ISO-datum vanaf wanneer de lijst geldt, of null. */
  geldigVanaf: string | null
  geldigTot: string | null
}

/** Eén dienst uit het werkschema (tijden + pauze), zoals de klant hem ziet. */
export interface KlantAfspraakDienstDTO {
  ploegSoort: string
  startTijd: string | null
  eindTijd: string | null
  pauzeMinuten: number | null
}

export interface KlantAfspraakWerkschemaDTO {
  naam: string
  shiftType: string
  /**
   * batch-69 (FO-511): de pauze per dienst is de FACTUURPAUZE uit de klantregel,
   * alleen gevuld als de pauzeregels bevestigd zijn (nooit de pauze van de monteur).
   */
  diensten: KlantAfspraakDienstDTO[]
  /** Klant-brede standaard factuurpauze in minuten (alleen als bevestigd), of null. */
  standaardPauzeMinuten: number | null
  /** batch-69 (FO-511): zijn de pauzeregels bevestigd? Anders "nog niet vastgelegd". */
  pauzeBevestigd: boolean
}

/**
 * De volledige klant-payload van de Afspraken-pagina. Elk blok is `null` zodra
 * de bijbehorende deel-vlag uit staat - dan bestaat het veld niet in de
 * payload, in plaats van leeg mee te reizen.
 */
export interface KlantAfsprakenDTO {
  /**
   * De tarieflijst raakte het plafond; de klant ziet dat expliciet in plaats van
   * stil een paar lijsten te missen.
   */
  tarievenAfgekapt: boolean
  toelichting: string | null
  betalingstermijn: { code: string; dagen: number | null } | null
  /**
   * Alleen de route-CODE (`FINQLE`, `EIGEN`, `HYBRIDE`, ...). De klant-app is
   * tweetalig en vertaalt hem via next-intl; de API levert bewust geen
   * kant-en-klare zin, anders krijgt een Engelse klant Nederlands.
   */
  facturatieroute: { code: string } | null
  tarieven: KlantAfspraakTariefDTO[] | null
  werkschema: KlantAfspraakWerkschemaDTO | null
}

/** `PaymentCondition` -> aantal dagen. Null bij een onbekende waarde. */
export function betalingstermijnDagen(code: string | null | undefined): number | null {
  if (!code) return null
  const m = /^DAGEN_(\d+)$/.exec(code)
  return m ? Number(m[1]) : null
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-klant zichtbaarheid van een individuele monteur
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `Employee.isVisibleToClient` is een GLOBALE vlag: hem uitzetten haalt de
 * monteur bij álle klanten weg. Er was geen manier om iemand bij één klant te
 * verbergen, terwijl dat juist de vraag is ("die monteur komt hier niet meer").
 *
 * Deze payload zet de per-klant uitzondering. Strengste-wint: `verborgen=false`
 * zet een eerdere uitzondering terug, maar haalt nooit de globale vlag weg.
 */
export const klantMonteurZichtbaarheidSchema = z
  .object({
    employeeId: z.string().trim().min(1, 'Kies een monteur'),
    verborgen: z.boolean(),
    reden: z.string().trim().max(1000).nullable().optional(),
  })
  .refine((v) => !v.verborgen || (v.reden ?? '').trim().length >= 3, {
    message: 'Geef kort aan waarom deze monteur voor deze klant verborgen wordt',
    path: ['reden'],
  })
export type KlantMonteurZichtbaarheidInput = z.infer<typeof klantMonteurZichtbaarheidSchema>

/** Eén monteur in het beheer-overzicht op de relatie-tab Klantenportaal. */
export interface KlantMonteurZichtbaarheidRow {
  employeeId: string
  naam: string
  functionTitle: string | null
  /** Aantal dagen dat deze monteur bij deze klant is ingezet. */
  inzetDagen: number
  /** ISO-datum van de laatste inzet, of null. */
  laatsteInzet: string | null
  /** `Employee.isVisibleToClient` - de globale vlag. */
  globaalZichtbaar: boolean
  /** Staat er een per-klant uitzondering, en zo ja: verbergt die hem? */
  verborgenVoorDezeKlant: boolean
  reden: string | null
  gewijzigdOp: string | null
  gewijzigdDoor: string | null
  /** Het eindoordeel dat de klant-API hanteert (strengste-wint). */
  zichtbaarVoorKlant: boolean
  /**
   * batch-62 klant-vaste-inzet (FO-458) - hoort bij de vaste inzet van deze
   * klant; de monteurspagina toont hem dan ook zonder inzet-historie.
   */
  inVasteInzet: boolean
}
